import React, { PropsWithChildren } from 'react'
import classNames from 'classnames'

import { Loading } from '../../../global/Loading/Loading'
import { ModalDesktop } from './ModalDesktop'
import { ModalTitle } from './common/components/ModalTitle'
import { cloneButtonDesktop, isButtonValid } from './common/helpers/button'
import { CommonModalTitleProps, ModalProps } from './types'

import styles from './DialogModal.module.scss'

interface DialogModalProps extends PropsWithChildren<CommonModalTitleProps & ModalProps> {
  isLoading?: boolean
  renderFooter?(props: DialogModalProps): React.ReactNode[]
}

export const DialogModal: React.FC<DialogModalProps> & {
  Wrapper: WrapperType
  Header: HeaderType
  Body: BodyType
  BodyText: BodyType
} = (props) => {
  const { title, isLoading = false, children } = props

  return (
    <DialogModal.Wrapper {...props} header={<DialogModal.Header title={title} />}>
      {isLoading && (
        <DialogModal.BodyText>
          <Loading isLoading={isLoading} showLoadingIcon />
        </DialogModal.BodyText>
      )}
      {!isLoading && <DialogModal.BodyText>{children}</DialogModal.BodyText>}
    </DialogModal.Wrapper>
  )
}

type WrapperType = React.FC<
  PropsWithChildren<Omit<DialogModalProps & { header: React.ReactNode }, 'title'>>
>

DialogModal.Wrapper = (props) => {
  const { size, onClose, isLoading, renderFooter, header, children } = props

  return (
    <ModalDesktop size={size} isLoading={isLoading} onClose={onClose}>
      {header}
      {isLoading && (
        <DialogModal.BodyText>
          <Loading isLoading={isLoading} showLoadingIcon />
        </DialogModal.BodyText>
      )}
      {!isLoading && <>{children}</>}
      {isLoading && <DialogModalFooter buttons={[]} />}
      {!isLoading && <DialogModalFooter buttons={renderFooter?.({ ...props, onClose })} />}
    </ModalDesktop>
  )
}

type HeaderType = React.FC<PropsWithChildren<CommonModalTitleProps>>

DialogModal.Header = (props) => {
  const { title } = props

  return (
    <>
      {title && (
        <header className={classNames('modal-card-head', styles.header)}>
          <span className={classNames('modal-card-title', styles.title)}>
            <ModalTitle title={title} />
          </span>
        </header>
      )}
      {!title && <header className={classNames('modal-card-head', styles.headerNoTitle)} />}
    </>
  )
}

type BodyType = React.FC<PropsWithChildren>

DialogModal.Body = (props) => {
  const { children } = props

  return (
    <section id='body-modal' className={classNames('modal-card-body', styles.body)}>
      {children}
    </section>
  )
}

DialogModal.BodyText = (props) => {
  const { children } = props

  return (
    <section id='body-modal' className={classNames('modal-card-body', styles.bodyText)}>
      {children}
    </section>
  )
}

interface DialogModalDesktopFooterProps {
  buttons?: React.ReactNode[]
}

const DialogModalFooter: React.FC<DialogModalDesktopFooterProps> = (props) => {
  const { buttons = [] } = props

  if (buttons?.length > 0) {
    return (
      <footer className={classNames('modal-card-foot', styles.footer)}>
        {buttons.map((button, index) => {
          return isButtonValid(button) ? (
            cloneButtonDesktop(button)
          ) : (
            <span key={index}>{button}</span>
          )
        })}
      </footer>
    )
  }

  return <footer className={classNames('modal-card-foot', styles.footer)} />
}
